import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MemberGate } from "@/components/RequestDialogs";
import { useAuth } from "@/context/AuthContext";
import { DatePicker } from "@/components/DatePicker";
import { api, formatErr, isMember } from "@/lib/api";

const DEFAULT_TERMS = "Placement licence: the candidate is engaged through VirtuForce and licensed to work for the business for the agreed hours. Work product belongs to the business. Either party may end the engagement with 14 days notice.";

export const OfferDialog = ({ candidate, open, onOpenChange }) => {
  const { user } = useAuth();
  const [complete, setComplete] = useState(true);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !candidate) return;
    setF({ position: candidate.title, engagement: candidate.availability || "Full-time", hours_per_week: 40, hourly_rate: candidate.hourly_rate,
      start_date: "", message: "", licensing_fee: 0, license_terms: DEFAULT_TERMS });
    api.get("/business/profile").then((r) => setComplete(r.data.complete)).catch(() => {});
  }, [open, candidate]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/offers", { ...f, candidate_id: candidate.id, hours_per_week: Number(f.hours_per_week), hourly_rate: Number(f.hourly_rate), licensing_fee: Number(f.licensing_fee) });
      toast.success("Offer sent to candidate");
      onOpenChange(false);
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const body = !isMember(user) ? <MemberGate compact /> : !complete ? (
    <div data-testid="profile-incomplete-gate" className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
      Candidates need to know who they'll work for. Please complete your business profile before sending offers.
      <Link to="/app/business-profile" className="mt-2 block font-medium underline" data-testid="complete-profile-link">Complete business profile</Link>
    </div>
  ) : (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-1.5 sm:col-span-2"><Label>Position</Label><Input data-testid="offer-position-input" value={f.position || ""} onChange={set("position")} /></div>
      <div className="space-y-1.5"><Label>Engagement</Label>
        <Select value={f.engagement} onValueChange={set("engagement")}><SelectTrigger data-testid="offer-engagement-select"><SelectValue /></SelectTrigger>
          <SelectContent>{["Full-time", "Part-time", "Project-based"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
      <div className="space-y-1.5"><Label>Hours / week</Label><Input data-testid="offer-hours-input" type="number" value={f.hours_per_week ?? ""} onChange={set("hours_per_week")} /></div>
      <div className="space-y-1.5"><Label>Hourly rate (USD)</Label><Input data-testid="offer-rate-input" type="number" value={f.hourly_rate ?? ""} onChange={set("hourly_rate")} /></div>
      <div className="space-y-1.5"><Label>Start date</Label><DatePicker testId="offer-start-input" value={f.start_date || ""} onChange={set("start_date")} /></div>
      <div className="space-y-1.5 sm:col-span-2"><Label>Message to candidate</Label><Textarea data-testid="offer-message-input" rows={2} value={f.message || ""} onChange={set("message")} /></div>
      <div className="space-y-1.5"><Label>Licensing / placement fee (USD, one-off)</Label><Input data-testid="offer-fee-input" type="number" value={f.licensing_fee ?? 0} onChange={set("licensing_fee")} /></div>
      <div className="space-y-1.5 sm:col-span-2"><Label>Licence / contract terms</Label><Textarea data-testid="offer-terms-input" rows={4} value={f.license_terms || ""} onChange={set("license_terms")} /></div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="offer-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Send offer to {candidate?.name}</DialogTitle>
          <DialogDescription>The candidate can review your business profile and accept or decline from their portal.</DialogDescription>
        </DialogHeader>
        {body}
        {isMember(user) && complete && <DialogFooter><Button data-testid="offer-submit-button" disabled={busy || !f.position} onClick={send}>{busy ? "Sending…" : "Send offer"}</Button></DialogFooter>}
      </DialogContent>
    </Dialog>
  );
};
